/**
 * How many nights a room charge is for.
 *
 * A hotel bills the nights a guest has had, not the number they gave at the
 * desk on the way in. Those are the same right up until somebody stays on, and
 * guests stay on all the time - a flight moves, a wedding runs long, a room is
 * held another day. The number taken at check-in is an expectation; the folio
 * has to follow the room.
 *
 * Nights are counted as calendar boundaries crossed in the hotel's own time
 * zone, which is how a hotel counts them: arriving at one in the afternoon and
 * leaving at ten the next morning is one night, not "under 24 hours, so none".
 */
/**
 * The calendar date at an instant, where the hotel is: "2026-08-27".
 *
 * The time zone is a property setting, typed in by a person, so an unusable one
 * falls back to UTC rather than throwing - a folio must still open.
 */
export function localDate(at, timeZone) {
    try {
        // en-CA gives ISO order, which is what makes these directly comparable.
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: timeZone || 'UTC',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(at);
    }
    catch {
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: 'UTC',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(at);
    }
}
const MS_PER_DAY = 24 * 60 * 60 * 1000;
/**
 * Nights between two moments: the number of date boundaries crossed.
 *
 * Compared as dates rather than as elapsed hours, because that is what a night
 * is to a hotel. Never negative - a folio read against a clock that has slipped
 * should show nothing owed, not a credit.
 */
export function nightsBetween(from, to, timeZone) {
    const start = Date.parse(`${localDate(from, timeZone)}T00:00:00Z`);
    const end = Date.parse(`${localDate(to, timeZone)}T00:00:00Z`);
    return Math.max(0, Math.round((end - start) / MS_PER_DAY));
}
/**
 * The nights a folio should charge for, as of `now`.
 *
 * The greater of what was booked and what has actually been slept, and never
 * less than one:
 *
 *  - staying on bills the extra nights, which is the whole point of this;
 *  - leaving early still bills what was agreed at the desk, because giving a
 *    night back is a decision somebody makes, not something a clock does;
 *  - a stay that has been checked out is frozen, so an old folio reads the same
 *    next week as it did on the day.
 */
export function chargeableNights(stay, now, timeZone) {
    const until = stay.checkOutAt ?? now;
    return Math.max(1, stay.bookedNights, nightsBetween(stay.checkInAt, until, timeZone));
}
//# sourceMappingURL=stay.js.map